/**
 * Schaltet zwischen den drei Dienstplan-Ansichten um (Wochenplan/Mitarbeiter/Bedarf, siehe
 * reference/bedarfsplaner/BEDARFSPLANER.md und reference/crispy-wiggling-balloon.md Phase 9). Alle
 * drei Ansichten teilen sich die Appbar/Toolbar/Filterbar (siehe Dienstplaner.html) — nur der jeweilige
 * Grid-Container (#grid/#magrid/#bedarfgrid) und ein paar ansichtsspezifische Toolbar-Controls
 * (Wochen- vs. Monatsnavigation, Bereich/Posten-Level-Toggle, Vorlagen-Sidebar) werden ein-/
 * ausgeblendet. Ein Tab-Wechsel rendert die Zielansicht immer frisch (keine Stale-Hidden-DOM-Fälle zu
 * berücksichtigen, kein Doppel-Rendern beim initialen Laden).
 */

import { zeigeDienstplan } from '../dienstplan.js';
import { zeigeMitarbeiteransicht } from './mitarbeiteransichtView.js';
import { zeigeBedarfsplaner, initBedarfsplanerToolbar, aktualisiereKostenToolbar } from './bedarfsplanerView.js';

const grid = document.getElementById('grid');
const magrid = document.getElementById('magrid');
const bedarfgrid = document.getElementById('bedarfgrid');
const tabWochenplan = document.getElementById('tab-wochenplan');
const tabMitarbeiter = document.getElementById('tab-mitarbeiter');
const tabBedarf = document.getElementById('tab-bedarf');
const freigabeMitarbeiterBtn = document.getElementById('ma-freigabe-btn');
const weeknav = document.getElementById('weeknav');
const btnHeute = document.getElementById('btn-heute');
const bedarfMonatnav = document.getElementById('bedarf-monatnav');
const bedarfHeuteBtn = document.getElementById('bedarf-heute-btn');
const bedarfTagesvorlagenBtn = document.getElementById('bedarf-tagesvorlagen-btn');
const bedarfEventBtn = document.getElementById('bedarf-event-btn');
const levelseg = document.getElementById('levelseg');
const filterSuche = document.getElementById('filter-suche-sektion');
const vorlagenSidebar = document.getElementById('vorlagen-uebersicht');

/**
 * Wochenplan-/Mitarbeiter-spezifische Toolbar-Controls, die in der Bedarf-Ansicht (Monatsansicht, eigene
 * Toolbar laut BEDARFSPLANER.md §1) keinen Sinn ergeben und dort ausgeblendet werden — Vorlagen,
 * Woche-kopieren, Sortierung, Handbuch, Drucken. Wochenplan und Mitarbeiteransicht teilen sich diese
 * Buttons wie gehabt.
 */
// Der Vorlagen-Editor-Button sitzt in der Vorlagen-Sidebar und wird mit ihr ausgeblendet.
const wochenplanControls = ['kopieren-tb-div', 'sortier-gruppe', 'kal-help', 'kal-pdf']
	.map((id) => document.getElementById(id))
	.filter(Boolean);

let aktiveView = 'wochenplan';
let bedarfToolbarInitialisiert = false;

function getAktiveView() {
	return aktiveView;
}

/**
 * Spiegelt die aktive Ansicht in der URL (`?view=mitarbeiter`), damit ein direkter Link/Reload wieder
 * in derselben Ansicht landet. Kein voller Navigationseintrag, nur ein stiller State-Ersatz.
 */
function aktualisiereUrl() {
	const url = new URL(window.location);
	if (aktiveView === 'wochenplan') {
		url.searchParams.delete('view');
	} else {
		url.searchParams.set('view', aktiveView);
	}
	window.history.replaceState({}, '', url);
}

/**
 * Wechselt die aktive Ansicht und rendert sie frisch. Setzt zusätzlich alle "Woche kopieren"/Freigabe-
 * Auswahl-Checkboxen (`.ma-auswahl`) zurück — sonst könnte eine in der verlassenen Ansicht angehakte
 * Auswahl unbemerkt in eine Aktion der neuen Ansicht einfließen (beide Grids können eigene .ma-auswahl
 * Checkboxen für dieselben Mitarbeitenden enthalten).
 * @param {'wochenplan'|'mitarbeiter'|'bedarf'} view
 */
async function wechsleView(view) {
	if (view === aktiveView) return;
	document.querySelectorAll('.ma-auswahl').forEach((checkbox) => { checkbox.checked = false; });
	aktiveView = view;
	tabWochenplan?.classList.toggle('active', view === 'wochenplan');
	tabMitarbeiter?.classList.toggle('active', view === 'mitarbeiter');
	tabBedarf?.classList.toggle('active', view === 'bedarf');
	grid?.classList.toggle('view-hidden', view !== 'wochenplan');
	magrid?.classList.toggle('view-hidden', view !== 'mitarbeiter');
	bedarfgrid?.classList.toggle('view-hidden', view !== 'bedarf');
	freigabeMitarbeiterBtn?.classList.toggle('view-hidden', view !== 'mitarbeiter');
	// Wochenplan-Wochennavigation/-Heute vs. Bedarf-Monatsnavigation/-Heute + Bereich/Posten-Umschalter.
	weeknav?.classList.toggle('view-hidden', view === 'bedarf');
	btnHeute?.classList.toggle('view-hidden', view === 'bedarf');
	bedarfMonatnav?.classList.toggle('view-hidden', view !== 'bedarf');
	bedarfHeuteBtn?.classList.toggle('view-hidden', view !== 'bedarf');
	bedarfTagesvorlagenBtn?.classList.toggle('view-hidden', view !== 'bedarf');
	bedarfEventBtn?.classList.toggle('view-hidden', view !== 'bedarf');
	levelseg?.classList.toggle('view-hidden', view !== 'bedarf');
	// Besetzung/Kosten-Umschalter, Kosten-Legende und Monatssumme (nur Budget-Rollen, siehe bedarfsplanerView.js)
	aktualisiereKostenToolbar(view === 'bedarf');
	// Die Suche (Schichttitel/Name/Personalnummer) filtert Mitarbeiterzeilen — die gibt es im Bedarfsplaner nicht.
	filterSuche?.classList.toggle('view-hidden', view === 'bedarf');
	// Vorlagen-/Kopieren-/Sortier-/Druck-Controls und die Vorlagen-Sidebar gehören nur zur Wochen-/
	// Mitarbeiteransicht — in der Bedarf-Ansicht (eigene, schlanke Toolbar) ausblenden.
	wochenplanControls.forEach((el) => el.classList.toggle('view-hidden', view === 'bedarf'));
	vorlagenSidebar?.classList.toggle('view-hidden', view === 'bedarf');
	if (view === 'bedarf' && !bedarfToolbarInitialisiert) {
		initBedarfsplanerToolbar();
		bedarfToolbarInitialisiert = true;
	}
	aktualisiereUrl();
	await renderAktiveView();
}

/**
 * Rendert die aktuell aktive Ansicht neu — für Aktionen, die "den gerade sichtbaren Dienstplan
 * aktualisieren" wollen, ohne selbst wissen zu müssen, welche Ansicht das gerade ist (Wochennavigation,
 * Sortierung, Woche-kopieren, Urlaubsantrag-Entscheidung, Filter zurücksetzen/anwenden, ...).
 * @param {{maRefresh?: boolean}} options
 */
async function renderAktiveView({ maRefresh = false } = {}) {
	if (aktiveView === 'mitarbeiter') {
		return zeigeMitarbeiteransicht(false, maRefresh);
	}
	if (aktiveView === 'bedarf') {
		return zeigeBedarfsplaner(false);
	}
	return zeigeDienstplan(true, false, false, maRefresh);
}

/**
 * Verdrahtet die Tab-Klicks und stellt eine über die URL angeforderte Ansicht wieder her. Wird erst
 * aufgerufen, nachdem der initiale Wochenplan-Boot-Render (siehe index.js:initDienstplan) durchgelaufen
 * ist — der befüllt u.a. #bereich-select/den Vorlagen-Toolbar-Select, von denen die Mitarbeiteransicht
 * (liest nur bereits gecachte stammdatenService-Daten) mit abhängt.
 */
function init() {
	tabWochenplan?.addEventListener('click', () => wechsleView('wochenplan'));
	tabMitarbeiter?.addEventListener('click', () => wechsleView('mitarbeiter'));
	tabBedarf?.addEventListener('click', () => wechsleView('bedarf'));
	const params = new URLSearchParams(window.location.search);
	if (params.get('view') === 'mitarbeiter') {
		wechsleView('mitarbeiter');
	} else if (params.get('view') === 'bedarf') {
		wechsleView('bedarf');
	}
}

const viewSwitcherFun = { wechsleView, renderAktiveView, getAktiveView, init };

export { init, wechsleView, renderAktiveView, getAktiveView, viewSwitcherFun };
